// Creating a team (RFC-1371 D1).
//
// Name only. `member_limit` defaults to 30 server-side (D9) and is Owner-only
// to change afterwards, so asking for it at creation would put an Owner-only
// setting in front of someone who is not an Owner yet and has no basis to pick
// a number.
//
// The new team becomes the active one on success. Creating a team and then
// having to find it in a switcher is a step with no purpose — the reason you
// made it is to work in it.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '../../analytics/events'
import type { TeamCreateEntryPoint } from '../../analytics/generated/analyticsEvents'
import { TeamsRequestError, type Team } from '../../api/teams'
import { useCreateTeam } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { BackButton, ErrorText, PrimaryButton, SecondaryButton } from './teamsChrome'

export default function CreateTeamView({
  onCreated,
  onCancel,
  entryPoint,
}: {
  onCreated: (team: Team) => void
  onCancel: () => void
  /** Where the create form was opened from, for `create_team_clicked`. */
  entryPoint: TeamCreateEntryPoint
}) {
  const { t } = useTranslation('settings')
  const createTeam = useCreateTeam()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Matches the schema's `CHECK (length(trim(name)) > 0)` so a name of spaces
  // fails here with readable copy rather than as a 400 from a constraint.
  const trimmed = name.trim()
  const canSubmit = trimmed.length > 0 && !createTeam.isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    // An ENABLED submit only (the guard above). Whether the team was actually
    // created is read from the teams table, not logged here (RFC-1577 §6).
    track('create_team_clicked', { entry_point: entryPoint })
    setError(null)
    createTeam.mutate(trimmed, {
      onSuccess: onCreated,
      onError: (err) => {
        const specific = err instanceof TeamsRequestError ? err.message_ : null
        setError(specific ?? t('settingsModal.teams.createFailed'))
      },
    })
  }

  return (
    <form
      className="flex flex-col gap-4"
      data-testid="teams-create-view"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <BackButton
        testId="teams-create-back"
        label={t('settingsModal.teams.back')}
        onClick={onCancel}
      />

      <h4 className="text-lg font-semibold text-white">
        {t('settingsModal.teams.createTitle')}
      </h4>
      <p className="text-sm leading-6 text-white/60">
        {t('settingsModal.teams.createDescription')}
      </p>

      <label className="flex flex-col gap-1.5 text-sm text-white/70">
        {t('settingsModal.teams.teamName')}
        <input
          type="text"
          data-testid="teams-create-name-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={80}
          autoComplete="off"
          className={cn(
            'h-9 rounded-lg border border-white/15 bg-black/30 px-3 text-sm text-white',
            'placeholder:text-white/30',
            'focus:outline-none focus:ring-2 focus:ring-white/30',
          )}
        />
      </label>

      <div className="flex justify-end gap-2">
        <SecondaryButton testId="teams-create-cancel" onClick={onCancel}>
          {t('settingsModal.teams.cancel')}
        </SecondaryButton>
        <PrimaryButton type="submit" testId="teams-create-submit" disabled={!canSubmit}>
          {t('settingsModal.teams.createTeam')}
        </PrimaryButton>
      </div>

      {error !== null && <ErrorText testId="teams-create-error">{error}</ErrorText>}
    </form>
  )
}
